<script setup lang="ts">
    import { formatLength } from '@/util/format/formatLength';
    import { formatDate } from '@/util/format/formatDate';
    import { PlanType } from '@/views/plan/store/types';
    import { computed } from 'vue';
    import PlanDetailItem from '@/views/plan/PlanDetailItem.vue';
    import PlanActions from '@/views/plan/PlanActions.vue';
    import { DateTime } from 'luxon';
    import { formatAngle } from '@/util/format/formatAngle';
    import {
        formatStemLabel,
        formatStemProfile,
        formatStemSystem,
        stemTypeInfo,
    } from '@/domain/catalog/stem';
    import {
        type AcetabularAngles,
        anatomicAngles,
        toRadiographic,
    } from '@/domain/anatomy/pelvis/acetabularAngles';
    import { toDegrees, toRadians } from '@/domain/anatomy/pelvis/anteversionInclination';
    import type { ApiPlan, ApiPlansCase } from '@/views/plan/api/types';
    import { planCupAlignment, planPatientName, planStem } from '@/views/plan/api/planCardSources';
    import { formatNumberSign } from '@/util/format/formatNumberSign';
    import QRCodeContainer from '@/views/plan/qr-code/QRCodeContainer.vue';
    import { usePlansStore } from '@/views/plan/store/store';
    import PlanDetailItemBox from '@/views/plan/PlanDetailItemBox.vue';
    import { adapterTypeSize, adapterTypeString } from '@/domain/catalog/head';
    import { AlignmentMode } from '@/domain/anatomy/pelvis/alignment';

    export interface Props {
        project: ApiPlansCase;
        plan: ApiPlan;
    }

    const props = defineProps<Props>();
    const store = usePlansStore();
    const title = computed(() => {
        if (props.plan.type === PlanType.Manual) {
            return props.plan.approval_state === 'proposed'
                ? 'User Proposed Plan'
                : 'User Approved Plan';
        } else if (props.plan.type === PlanType.Automated) {
            return 'Formus Plan';
        } else {
            return '';
        }
    });

    /**
     * Who produced the plan.
     *
     * The name alone, and 'created' rather than the act: the card title above already says whether
     * the plan was proposed or approved, so repeating it here would say the same thing twice.
     * Absent on plans that predate the field, and on Formus plans, which nobody produced.
     */
    const attribution = computed(() =>
        props.plan.type === PlanType.Manual && props.plan.created_by_name
            ? `created by ${props.plan.created_by_name}`
            : '',
    );

    const formattedDate = computed(() => {
        return formatDate(DateTime.fromISO(props.plan.created_on));
    });

    const refNumber = computed(() => {
        return props.plan.reference_number;
    });

    const formattedLesserTrochanterDistance = computed(() => {
        const measurements = props.plan.measurements;
        if (measurements) {
            const lesserTrochanterDistance = measurements.resection_distances_lesser_trochanter;
            return formatLength(lesserTrochanterDistance, 0);
        }
        return '--';
    });

    const formattedGreaterTrochanterDistance = computed(() => {
        const measurements = props.plan.measurements;
        if (measurements) {
            const greaterTrochanterDistance = measurements.resection_distances_greater_trochanter;
            return formatLength(greaterTrochanterDistance, 0);
        }
        return '--';
    });

    const formattedSaddleDistance = computed(() => {
        const measurements = props.plan.measurements;
        if (measurements) {
            const saddleDistance = measurements.resection_distances_saddle;
            return formatLength(saddleDistance, 0);
        }
        return '--';
    });

    const displayQrCode = computed(() => {
        return planCupAlignment(store.state.specifications?.cup_align_mode) === AlignmentMode.APP;
    });

    const formattedStemShape = computed((): string => {
        const stem = planStem(props.plan.template);
        return stem ? `(${formatStemProfile(stemTypeInfo(stem.type).profile)})` : '';
    });

    const formattedSystem = computed((): string => {
        const stem = planStem(props.plan.template);
        return stem ? formatStemSystem(stem.system) : '';
    });

    const formattedStemDescription = computed((): string => {
        const stem = planStem(props.plan.template);
        return stem ? formatStemLabel(stem) : '';
    });

    const formattedStemAnteversion = computed((): string => {
        const measurements = props.plan.measurements;
        if (measurements) {
            return formatAngle(measurements.stem_angle_anteversion ?? null);
        }
        return '--';
    });

    const formattedCupSize = computed((): string => {
        return props.plan.template.cup?.size ?? '';
    });

    const formattedHeadDescription = computed((): string => {
        const head = props.plan.template.head;
        if (head) {
            const size = head.size;
            const offset = formatNumberSign(head.offset);
            if (size && offset) {
                if (offset === adapterTypeSize) {
                    return `${formatLength(parseInt(size), 0)} ${offset} mm Offset ${adapterTypeString}`;
                }
                return `${formatLength(parseInt(size), 0)} ${offset} mm Offset`;
            }
        }

        return '';
    });

    const formattedLinerDescription = computed((): string => {
        const liner = props.plan.template.liner;
        const size = liner?.size;
        const type = liner?.type;

        if (size && type) {
            return `${size} (${type})`;
        }

        return '';
    });

    const radiographicRotation = computed((): AcetabularAngles<'radiographic'> => {
        const anatomicAnglesRadians = anatomicAngles(toRadians(props.plan.template.cup_rotation));
        return toDegrees(toRadiographic(anatomicAnglesRadians));
    });

    const formattedCupAnteversion = computed((): string => {
        return formatAngle(radiographicRotation.value?.anteversion ?? null);
    });

    const formattedCupInclination = computed((): string => {
        return formatAngle(radiographicRotation.value?.inclination ?? null);
    });

    const formattedPatientName = computed((): string => {
        return planPatientName(props.project.patient);
    });

    const formattedCaseNumber = computed(() => {
        const id = props.project.id;
        return id ? String(id) : '';
    });
</script>

<template>
    <app-main-card class="app-plan-card-wrapper">
        <v-card-item class="px-0 app-plan-card-title">
            <template #title>
                <div class="fill-height">
                    <div class="text-primary text-heading-h5">{{ title }}</div>
                    <div
                        v-if="attribution"
                        class="text-secondary-text text-body-2"
                        data-testid="plan-card-attribution"
                    >
                        {{ attribution }}
                    </div>
                    <div
                        class="text-secondary-text text-heading-h4"
                        data-testid="plan-card-project-name"
                    >
                        {{ project.name }}
                    </div>
                </div>
            </template>
            <template #append>
                <QRCodeContainer v-if="displayQrCode" :project="project" :plan="plan" />
            </template>
        </v-card-item>
        <v-row no-gutters class="mb-4">
            <div class="text-secondary text-heading-h5">{{ project.description }}</div>
            <v-spacer />
            <div class="text-secondary-text" data-testid="plan-card-ref-number">
                PLAN REFERENCE NUMBER: {{ refNumber }}
            </div>
        </v-row>
        <div class="app-plan-card-content mb-4">
            <v-row class="" no-gutters>
                <v-col cols="6">
                    <PlanDetailItem
                        label="Formus Case Number"
                        :value="formattedCaseNumber"
                        data-testid="plan-card-case-number"
                        class=""
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="Patient"
                        :value="formattedPatientName"
                        data-testid="plan-card-patient-name"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="G7 CUP"
                        :value="`Size ${formattedCupSize}`"
                        data-testid="plan-card-cup-size"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="G7 Liner"
                        :value="formattedLinerDescription"
                        data-testid="plan-card-cup-liner"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem label="Cup anteversion / inclination">
                        <div class="d-flex">
                            <PlanDetailItemBox
                                label="Anteversion"
                                class="mr-4"
                                :value="formattedCupAnteversion"
                                data-testid="plan-card-cup-anteversion"
                            />
                            <PlanDetailItemBox
                                label="Inclination"
                                :value="formattedCupInclination"
                                data-testid="plan-card-cup-inclination"
                            />
                        </div>
                    </PlanDetailItem>
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="Plan generated"
                        :value="formattedDate"
                        data-testid="plan-card-plan-generated-date"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        :label="`${formattedSystem} ${formattedStemShape}`"
                        :value="formattedStemDescription"
                        data-testid="plan-card-stem-description"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="Femoral Head"
                        :value="formattedHeadDescription"
                        data-testid="plan-card-head-description"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem
                        label="Stem Anteversion"
                        :value="formattedStemAnteversion"
                        data-testid="plan-card-stem-anteversion"
                    />
                </v-col>
                <v-col cols="6">
                    <PlanDetailItem label="Resection height (lt/gt/saddle)">
                        <div class="d-flex">
                            <PlanDetailItemBox
                                label="LT"
                                class="mr-4"
                                :value="formattedLesserTrochanterDistance"
                                data-testid="plan-card-lt-distance"
                            />
                            <PlanDetailItemBox
                                label="GT"
                                :value="formattedGreaterTrochanterDistance"
                                data-testid="plan-card-gt-distance"
                            />
                            <PlanDetailItemBox
                                label="Saddle"
                                :value="formattedSaddleDistance"
                                data-testid="plan-card-saddle-distance"
                            />
                        </div>
                    </PlanDetailItem>
                </v-col>
            </v-row>
        </div>

        <PlanActions :project="project" :plan="plan" />
    </app-main-card>
</template>

<style scoped>
    ul,
    li {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .app-plan-card-content {
        display: flex;
        flex-basis: 0;
        flex-grow: 1;
        flex-direction: column;
    }

    .app-plan-card-title :deep(.v-card-item__content) {
        height: 100% !important;
    }
</style>
